import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import Common from "../../../helper/common";
import AdvanceTable from "../../../components/advanceTable";
import {
  respEnum,
  SearchTypeEnum,
  StockLogTypeEnum,
  DateminFormat,
} from "../../../constants/appConstant";
import {
  AppSelect,
  AppSearchSectionSelect,
  AppInput,
} from "../../../components/appComponents";
import { useNavigate } from "react-router-dom";

const StockLogs = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [productid, setProductId] = useState(null);
  const [type, setType] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [tillDate, setTillDate] = useState(null);

  // Filter options - based on stock log types from your API
  const stockTypeOptions = [
    { id: null, name: "All Movement Types" },
    { id: StockLogTypeEnum.Stock_In, name: "Stock In" },
    { id: StockLogTypeEnum.Stock_Out, name: "Stock Out" },
  ];

  useEffect(() => {
    getData(1);
    getProducts();
  }, []);

  useEffect(() => {
    getData(1);
  }, [fromDate,tillDate, productid, type]);

  const resetFilters = () => {
    setProductId(null);
    setType(null);
    setFromDate(null);
    setTillDate(null);
  };

  const getProducts = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetProductsForPOS",
        {},
        "Get",
        false
      );
      if (response?.status === respEnum.Success) {
        const productOptions = response.data.map((product) => ({
          value: product.id,
          label: product.name,
        }));
        setProductsList([
          { value: null, label: "All Products" },
          ...productOptions,
        ]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const getData = async (
    pageNumber,
    searchTerm = "",
    recordsToDisplay = null
  ) => {
    
    if (fromDate === "") fromDate = null;
    if (tillDate === "") tillDate = null;

    const response = await Common.ApiService.getInstance().request(
      "GetStockLogs",
      {
        pageNumber: pageNumber,
        q: searchTerm,
        productid: productid,
        type: type,
        fromDate:fromDate,
        tillDate:tillDate,
        recordsToDisplay,
      },
      "POST"
    );

    if (response?.status === respEnum.Success && response?.data?.[0]) {
      const processedData = {
        ...response.data[0],
        data: response.data[0].data.map((item) => ({
          ...item,
          createdon: item.createdon
            ? Common.Utility.ConvertTime(item.createdon)
            : null,
            
            type_id:item.type,
            type:item.type ? Common.Utility.getKeyByValue(StockLogTypeEnum,item.type):null,


             hasRefId: item.refId != null, // true if refId exists

        })),
      };
      setData(processedData);
    }
  };

  const _addUpdateTrigger = () => (
    <Button size="icon" variant="outline" color="secondary" className="h-7 w-7">
      <Icon icon="heroicons:eye" className="h-4 w-4" />
    </Button>
  );

  const _onViewReference = async (refId) => {
    if (refId) {
      // Navigate to related order if reference exists
      navigate(`/app/pos-order-details?id=${refId}`);
    }
  };

  const actions = [
    {
      name: "view",
      icon: "heroicons:eye",
      tooltip: "View Reference",
      variant: "outline",
      size: "icon",
      className: "h-7 w-7",
      onClick: (row) => _onViewReference(row.refId),
      // condition: (row) => row.refId !=null, // Only show if reference exists
      visibleProp: "hasRefId",
      visibleValue: true,
    },
  ];

  const getStockTypeBadge = (type) => {
    switch (type) {
      case StockLogTypeEnum.Stock_In:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Icon icon="heroicons:arrow-up" className="w-3 h-3 mr-1" />
            Stock In
          </span>
        );
      case StockLogTypeEnum.Stock_Out:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <Icon icon="heroicons:arrow-down" className="w-3 h-3 mr-1" />
            Stock Out
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const getQuantityDisplay = (quantity) => {
    if (quantity > 0) {
      return (
        <span className="inline-flex items-center text-green-600 font-medium">
          <Icon icon="heroicons:plus" className="w-4 h-4 mr-1" />
          {quantity}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center text-red-600 font-medium">
          <Icon icon="heroicons:minus" className="w-4 h-4 mr-1" />
          {Math.abs(quantity)}
        </span>
      );
    }
  };

  const _thead = [
    {
      key: "productName",
      title: "Product",
      type: "text",
      isVisible: true,
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.productName || `Product #${row.productid}`}
          </span>
          {/* <span className="text-xs text-gray-500">ID: {row.productid}</span> */}
        </div>
      ),
    },
    {
      key: "type",
      title: "Movement Type",
      type: "text",
      isVisible: true,
      render: (row) => getStockTypeBadge(row.type_id),
    },
    {
      key: "quantity",
      title: "Quantity",
      type: "number",
      isVisible: true,
      render: (row) => getQuantityDisplay(row.quantity),
    },
    {
      key: "refId",
      title: "Reference",
      type: "text",
      isVisible: false,
      render: (row) => (
        <div className="flex flex-col">
          {row.refId ? (
            <>
              <span className="text-xs text-gray-500">Ref ID:</span>
              <span className="font-medium text-blue-600">#{row.refId}</span>
            </>
          ) : (
            <span className="text-gray-400 text-sm">No Reference</span>
          )}
        </div>
      ),
    },
    {
      key: "remarks",
      title: "Remarks",
      type: "text",
      isVisible: true,
      render: (row) => (
        <div className="max-w-xs">
          <span className="text-sm text-gray-700 break-words">
            {row.remarks || "No remarks"}
          </span>
        </div>
      ),
    },
    {
      key: "createdon",
      title: "Date & Time",
      type: "text",
      isVisible: true,
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.createdon || "N/A"}</span>
        </div>
      ),
    },
    {
      key: "createdByName",
      title: "Created By",
      type: "text",
      isVisible: true,
    },
  ];

  return (
    <>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4 p-4 bg-white rounded-lg shadow-sm border">

         <AppInput
             type={"date"}
             value={fromDate}
             onChange={(e) => {
               const selectedstartDate = e;
               if (tillDate && selectedstartDate > tillDate) {
                 Common.showToast(
                   "From Date must be lesser than till Date",
                   "Error",
                   "info"
                 );
                 setFromDate(null);
               } else {
                 setFromDate(selectedstartDate);
               }
             }}
             label="From"
             className="mb-3 col-span-1"
           />
           <AppInput
             type={"date"}
             value={tillDate}
             onChange={(e) => {
               const selectedEndDate = e;
               if (selectedEndDate < fromDate) {
                 Common.showToast("End Date must be greater", "Error", "info");
                 setTillDate(null);
               } else {
                 setTillDate(selectedEndDate);
               }
             }}
             label="Till"
             className="mb-3 col-span-1"
           />

        <AppSearchSectionSelect
          value={
            productsList.find((product) => product.value === productid) ||
            productsList[0]
          }
          onChange={(value) => setProductId(value?.value)}
          label="Product"
          data={productsList}
          className="col-span-1"
        />

        <AppSelect
          value={type}
          onChange={setType}
          label="Movement Type"
          data={stockTypeOptions}
          className="col-span-1"
        />

        <Button
          className="mt-6 h-8 col-span-1"
          onClick={resetFilters}
          variant="outline"
        >
          Reset Filters
        </Button>

        <Button
          className="mt-6 h-8 col-span-1"
          onClick={() => navigate("/app/stock")}
        >
          Manage Stock
        </Button>
      </div>

      {data?.data && (
        <AdvanceTable
          title={"Stock Logs"}
          columns={_thead.filter((item) => item.isVisible)}
          data={data}
          getData={getData}
          search={SearchTypeEnum.Server_Search}
          searchInputClassName="custom-search-mobile"
          showPagination={true}
          actionButtons={actions}
          updateTrigger={_addUpdateTrigger}
          showExportoptions
          exportFileName={"Stock Logs"}
        />
      )}
    </>
  );
};

export default StockLogs;
